import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
	IStyleSettingsCarouselUploaderContext,
	StyleSettingsCarouselUploaderContext,
} from '../styleSettingsCarouselUploaderState/styleSettingsCarouselUploaderContext';
import { ICustomItemsOfGroups } from 'types';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'react-i18next';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import { CarouselSlideNew } from './CarouselSlideNew';
import { CarouselThumbNew } from './CarouselThumbNew';
import { UploadVideoNew } from './UploadVideoNew';

interface IProps {
	mediaItems: ICustomItemsOfGroups[];
	selectedIndex: number;
	isMakeRequest: boolean;
	isImageUploadHidden: boolean;
	isVideoUploadHidden: boolean;
	videoMaxFileSizeMB: number;
	onSelectedIndexChange: (index: number) => void;
	onDeleteItem: (translationId: string) => void;
	onUploadVideo: ({ sourceUrl, fileId }: { sourceUrl: string; fileId: string }) => void;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
}

export const UploaderNew: React.FC<IProps> = ({
	mediaItems,
	selectedIndex,
	isMakeRequest,
	isImageUploadHidden,
	isVideoUploadHidden,
	videoMaxFileSizeMB,
	onSelectedIndexChange,
	onDeleteItem,
	onUploadVideo,
	showGroupLoader,
	hideGroupLoader,
}) => {
	const { t } = useTranslation('form');
	const { changeTypeOfModalWindow } = useContext<IStyleSettingsCarouselUploaderContext>(
		StyleSettingsCarouselUploaderContext,
	);
	const [prevItemsLength, setPrevItemsLength] = useState<number>(mediaItems.length);
	const [mainViewportRef, embla] = useEmblaCarousel({ skipSnaps: false });
	const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({ containScroll: 'keepSnaps', dragFree: true });

	useEffect(() => {
		if (mediaItems.length) {
			if (mediaItems.length < prevItemsLength) {
				embla?.scrollTo(0);
			}
			embla?.reInit();
			emblaThumbs?.reInit();
		}
		setPrevItemsLength(mediaItems.length);
	}, [mediaItems]);

	const handleThumbClick = useCallback(
		(index: number) => {
			if (embla && emblaThumbs?.clickAllowed()) {
				embla.scrollTo(index);
			}
		},
		[embla, emblaThumbs],
	);

	const handleSelect = useCallback(() => {
		if (!embla || !emblaThumbs) {
			return;
		}
		emblaThumbs.scrollTo(embla.selectedScrollSnap());
		onSelectedIndexChange(embla.selectedScrollSnap());
	}, [embla, emblaThumbs]);

	useEffect(() => {
		if (!embla) {
			return;
		}
		handleSelect();
		embla.on('select', handleSelect);

		return () => {
			embla.off('select', handleSelect);
		};
	}, [embla, handleSelect]);

	useEffect(() => {
		if (!embla) {
			return;
		}
		const observer = new ResizeObserver(() => {
			embla.reInit();
			emblaThumbs?.reInit();
		});
		observer.observe(embla.rootNode());

		return () => {
			observer.disconnect();
		};
	}, [embla, emblaThumbs]);

	const openCropper = () => {
		changeTypeOfModalWindow('cropper');
	};

	return (
		<div className="flex flex-col gap-4">
			{mediaItems.length ? (
				<>
					<div className="overflow-hidden" ref={mainViewportRef}>
						<div className="-ml-4 flex">
							{mediaItems.map((item) => (
								<div key={item.translationId} className="min-w-full pl-4">
									<CarouselSlideNew item={item} isMakeRequest={isMakeRequest} />
								</div>
							))}
						</div>
					</div>
					<div className="flex items-center gap-4">
						<NewButton
							variant="ghost"
							size="small"
							icon={true}
							iconLeft={<Icon name={IconName.chevronLeft} size="lg" />}
							disabled={selectedIndex === 0}
							ariaLabel={t('previousButton')}
							testId="previousItemButton"
							onClick={() => embla?.scrollPrev()}
						/>
						<div className="min-w-0 flex-1 overflow-hidden" ref={thumbViewportRef}>
							<div className="-ml-4 flex">
								{mediaItems.map((item, index) => (
									<div key={item.translationId} className="basis-1/4 shrink-0 pl-4">
										<CarouselThumbNew
											item={item}
											isSelected={index === selectedIndex}
											isMakeRequest={isMakeRequest}
											onSelect={() => handleThumbClick(index)}
											onDelete={() => onDeleteItem(item.translationId)}
										/>
									</div>
								))}
							</div>
						</div>
						<NewButton
							variant="ghost"
							size="small"
							icon={true}
							iconLeft={<Icon name={IconName.chevronRight} size="lg" />}
							disabled={selectedIndex >= mediaItems.length - 1}
							ariaLabel={t('nextButton')}
							testId="nextItemButton"
							onClick={() => embla?.scrollNext()}
						/>
					</div>
				</>
			) : (
				<div className="h-96 rounded flex items-center justify-center bg-purple-01-new" data-test="NoItems">
					<Icon name={IconName.image} size="smallHero" className="text-purple-03-new" />
				</div>
			)}
			{(!isImageUploadHidden || !isVideoUploadHidden) && (
				<div className="flex gap-4">
					{!isImageUploadHidden && (
						<NewButton
							size="small"
							iconLeft={<Icon name={IconName.upload} size="lg" />}
							testId="uploadImageButton"
							onClick={openCropper}>
							<Text translationNameSpace="form" translationKey="uploadImageButton" asFragment={true} />
						</NewButton>
					)}
					{!isVideoUploadHidden && (
						<UploadVideoNew
							videoMaxFileSizeMB={videoMaxFileSizeMB}
							onUpload={onUploadVideo}
							showGroupLoader={showGroupLoader}
							hideGroupLoader={hideGroupLoader}
						/>
					)}
				</div>
			)}
		</div>
	);
};
